import { toast } from '@oy/uidesktop';
import * as Sentry from '@sentry/browser';
import { AxiosError } from 'axios';
import axios from 'src/clients/axios';
import { ENV } from 'src/constants';
import { Store } from 'src/redux/storeConfig/store';
import { storage } from '../Storage';
import { sanitizer } from '../sanitizer';
import { reportFunctionError } from '../sentry';
import { getTranslation } from '../translator';
import {
  LOGIN_ENDPOINT,
  MOCK_BASE_URL,
  OTP_ENDPOINT,
  STATUS_CODES,
} from './constants';
import ErrorTypes from './errorTypes';

let interceptReqId = 0;
let interceptResId = 0;

/**
 * if your purpose is mentioned below, better to add these configs in your axios request than edit this file
 *
 * @param config.disabledToast - disable showing toast when the response is error
 * @param config.onlyProduction - disable hit and using Staging Token/API Endpoint
 * @param config.onlyStaging - force hit and using Staging Token/API Endpoint
 */
export default {
  setup: (store: Store) => {
    interceptReqId = axios.interceptors.request.use(
      config => {
        const auth = storage.get('auth');
        const env = storage.get('env');
        const lang = storage.get('lang');
        const challenger = storage.get('challenger');

        const isStaging =
          (env === ENV.STAGING && !config.onlyProduction) || config.onlyStaging;

        // Lang Config for bilingual response from BE
        if (!lang) {
          storage.set('lang', 'id');
        }

        // GENERAL HEADER & TOKEN CONFIG
        if (config.headers) {
          /**
           * If you want to add another header,
           * please make sure to ask infra team to whitelist the new header
           * for dev, staging, and production to prevent CORS issue.
           */
          config.headers['Accept-Language'] = lang ?? 'id';
          config.headers['X-Oy-Platform'] = 'BUSINESS_DASHBOARD';

          if (challenger) {
            config.headers['X-Oy-Challenger-Token'] = challenger;
          }

          if (auth && auth.token) {
            const token = !isStaging ? auth.token : auth.tokenStaging;
            const username = !isStaging
              ? auth.userDefault?.username ?? auth.user?.username
              : auth.usernameStaging;

            config.headers['X-Oy-Authorization'] = `token=${token}`;
            config.headers['X-Oy-Username'] = username;
          }
        }

        // BASE URL CONFIG
        if (isStaging) {
          config.baseURL = import.meta.env.VITE_API_STAGING_URL;
        } else if (import.meta.env.VITE_ENV !== 'prod') {
          config.baseURL = config.mock
            ? MOCK_BASE_URL
            : store.getState().devConfig?.value ?? import.meta.env.VITE_API_URL;
        }

        // Sentry - add Breadcrumb for logging
        Sentry.addBreadcrumb({
          type: 'http',
          category: 'http.request',
          data: {
            method: (config.method || '').toUpperCase(),
            url: (config.baseURL || '') + (config.url || ''),
            body: JSON.stringify(config.data),
            params: JSON.stringify(config.params),
          },
          level: 'info',
        });

        // CONTENT TYPE DATA CONFIG
        if (config.data) {
          if (
            config.headers?.['Content-Type'] === 'application/json' ||
            Object.keys(config.data).length > 0
          ) {
            try {
              const bodyStr = JSON.stringify(config.data);
              const sanitizedStr = sanitizer(bodyStr);
              config.data = JSON.parse(sanitizedStr);
            } catch (exception) {
              reportFunctionError({
                domain: 'dashboard',
                message: 'Failed to sanitize JSON body',
                name: 'sanitizer',
                exception,
                parentName: 'interceptors',
              });
            }
          } else if (
            config.headers?.['Content-Type'] === 'multipart/form-data' ||
            config.data instanceof FormData
          ) {
            for (const [name, value] of config.data.entries()) {
              if (!(value instanceof File) || !(value instanceof Blob)) {
                config.data.set(name, sanitizer(value));
              }
            }
          }
        }

        // PARAMETER CONFIG
        if (config.params) {
          try {
            const queryStr = JSON.stringify(config.params);
            const sanitizedStr = sanitizer(queryStr);
            config.params = JSON.parse(sanitizedStr);
          } catch (exception) {
            reportFunctionError({
              domain: 'dashboard',
              message: 'Failed to sanitize query params',
              name: 'sanitizer',
              exception,
              parentName: 'interceptors',
            });
          }
        }

        return config;
      },
      (error: any) => {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(error);
        });
        return Promise.reject(error);
      },
    );

    // Add a response interceptor
    interceptResId = axios.interceptors.response.use(
      response => {
        const lang = storage.get('lang');
        const isRequestOTP = response.config.params?.isRequestOTP || false;
        const isPollingBulkValidation =
          response.config.params?.isPollingBulkValidation || false;

        // No need to show toast for request OTP.
        if (isRequestOTP) return response;

        // No need to show toast for async bulk invalid request id.
        if (
          isPollingBulkValidation &&
          response.data.error &&
          response.data.error.code === '406'
        ) {
          return response;
        }

        if (response.config.disableToast) return response;

        if (response.data) {
          const responseData = response.data;

          // Check for reasons
          if (responseData?.reason || responseData?.message) {
            if (
              responseData?.success &&
              (responseData?.reason || responseData?.message)
            ) {
              toast.success(responseData?.reason ?? responseData?.message);
            }
            if (
              !responseData.success &&
              responseData?.error?.code !== 500 &&
              responseData?.error?.message
            )
              toast.error(responseData.error.message);
          }

          // Check for success with custom messages
          if (responseData.data) {
            const data = responseData.data;

            if (data.message && data.message[lang ?? 'en']) {
              toast.success(data.message[lang ?? 'en'], { duration: 15000 });
            } else if (
              data.message &&
              responseData.status &&
              typeof data.message == 'string'
            ) {
              toast.success(data.message);
            }
          }

          // Check for generic error message
          if (responseData.message && !responseData.status) {
            toast.error(responseData.message);
          }
        }
        return response;
      },
      (error: AxiosError<any>) => {
        // See: https://axios-http.com/docs/handling_errors
        if (error.response && error.response.status !== 0) {
          if (
            error.response.status !== STATUS_CODES.EMPTY_TOKEN &&
            error.response.status !== STATUS_CODES.SESSION_EXPIRED
          ) {
            Sentry.addBreadcrumb({
              type: 'http',
              category: 'http.request',
              level: 'error',
              data: {
                method: (error?.config?.method || '').toUpperCase(),
                url:
                  (error?.config?.baseURL || '') + (error?.config?.url || ''),
                body: JSON.stringify(error?.config?.data),
                params: JSON.stringify(error?.config?.params),
                response: JSON.stringify(error.response.data),
                status_code: error.response.status,
              },
            });

            const forbiddenError = `403: ${
              (error?.config?.baseURL || '') + (error?.config?.url || '')
            }`;

            Sentry.withScope(scope => {
              scope.setLevel('error');
              Sentry.captureException(
                error!.response!.status !== STATUS_CODES.FORBIDDEN_ACCESS
                  ? error
                  : forbiddenError,
              );
            });
          }

          const auth = storage.get('auth');
          const isOtp = error?.config?.url?.endsWith(OTP_ENDPOINT) || false;
          const isLogin = error?.config?.url?.endsWith(LOGIN_ENDPOINT) || false;
          if (error.response.status === 500) {
            toast.error('Internal Server Error.');
          } else if (
            auth &&
            error.response.status === STATUS_CODES.SESSION_EXPIRED &&
            !isOtp &&
            !isLogin
          ) {
            store.dispatch({
              type: 'SET_EXPIRED_SESSION',
            });
            store.dispatch({
              type: 'RESET_ENV',
            });
          }

          //Process message error
          let message = '';
          const responseData = error.response.data;

          if (responseData?.reason) message = responseData?.reason;
          else if (responseData?.message) message = responseData?.message;
          else if (responseData?.error)
            message = `${responseData?.error?.code}, ${responseData?.error?.message}`;

          if (message && !error?.config?.disableToast) {
            toast.error(message);
          }

          error.type = ErrorTypes.SERVER_ERROR;
          return Promise.reject(error);
        } else if (error.request) {
          // The request was made but no response was received
          if (
            error.code !== AxiosError.ERR_NETWORK &&
            error.code !== AxiosError.ERR_CANCELED &&
            error.code !== AxiosError.ECONNABORTED
          ) {
            Sentry.addBreadcrumb({
              type: 'http',
              category: 'http.request',
              level: 'error',
              data: {
                method: (error?.config?.method || '').toUpperCase(),
                url:
                  (error?.config?.baseURL || '') + (error?.config?.url || ''),
                body: JSON.stringify(error?.config?.data),
                params: JSON.stringify(error?.config?.params),
              },
            });
            Sentry.withScope(scope => {
              scope.setLevel('error');
              Sentry.captureException(error);
            });
          }
          if (error.code === AxiosError.ERR_NETWORK && !navigator.onLine) {
            toast.error(getTranslation('error_pleaseCheckInternet'));
          }
          error.type = ErrorTypes.NETWORK_ERROR;
          return Promise.reject(error);
        } else {
          // Something happened in setting up the request that triggered an Error
          Sentry.withScope(scope => {
            scope.setLevel('error');
            Sentry.captureException(error);
          });
          if (error instanceof TypeError) {
            error.type = ErrorTypes.TYPE_ERROR;
            return Promise.reject(error);
          }
          error.type = ErrorTypes.UNKNOWN_ERROR;
          return Promise.reject(error);
        }
      },
    );
  },

  remove: () => {
    axios.interceptors.request.eject(interceptReqId);
    axios.interceptors.response.eject(interceptResId);
  },
};
